import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { appInstanceService } from '@common/services/appInstanceService';
import {
  signInTma,
  AuthFailureReason,
  tokenService,
} from '@common/services/tokenService';
import { Button } from '@common/ui/buttons/Button';
import { Loader } from '@common/ui/Loader';
import { Result } from '@common/ui/Result';
import { isMaintenanceError } from '@common/utils/fetcher';
import {
  nextRouteAfterAuth,
  unregisteredRoute,
} from '@common/utils/startParam';

import { applyAuthResponse } from '@entities/account';
import { isStorableUser, reportRefusedUser } from '@entities/auth/utils';
import {
  useFiltersStore,
  defaultPremiumFilters,
} from '@entities/dating/stores/useFiltersStore';

import { usePostAuth } from '@services/hooks/AuthService';

type Props = {
  children: React.ReactNode;
};

type AuthState =
  | 'initializing'
  | 'loading_profile'
  | 'ready'
  | 'not_registered'
  | 'maintenance'
  | 'error';

const ONE_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_MAINTENANCE_MESSAGE =
  'Приложение временно недоступно. Загляните чуть позже.';

export const AuthProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>('initializing');
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState(
    DEFAULT_MAINTENANCE_MESSAGE,
  );

  const {
    error: errorPostAuth,
    data: dataPostAuth,
    trigger: triggerPostAuth,
  } = usePostAuth();
  // NOTE: select only the setter (referentially stable) instead of subscribing
  // to the entire store. The previous `useFiltersStore()` form returned a new
  // state object on every set(), and including that object in the dataPostAuth
  // effect's deps caused an infinite re-render loop for non-premium users with
  // any filter set: the effect calls setFilters → store updates → new ref →
  // effect re-runs → calls setFilters again → "Maximum update depth exceeded"
  // → caught by the root error boundary as "Что-то пошло не так". See Sentry
  // issue #70294076 (top-frequency post-cutover; predominantly mobile because
  // Telegram WebApp evicts session state more often than desktop).
  const setFilters = useFiltersStore((s) => s.setFilters);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const schedulePremiumRefresh = useCallback(
    (premiumUntil?: string) => {
      if (!premiumUntil) return;

      const delay = new Date(premiumUntil).getTime() - Date.now();

      if (delay > 0 && delay < ONE_DAY) {
        timeoutRef.current = setTimeout(() => {
          void triggerPostAuth();
        }, delay);
      }
    },
    [triggerPostAuth],
  );

  const showMaintenance = useCallback((error: unknown) => {
    if (!isMaintenanceError(error)) {
      return false;
    }

    setMaintenanceMessage(error.message || DEFAULT_MAINTENANCE_MESSAGE);
    setAuthState('maintenance');

    return true;
  }, []);

  useEffect(() => {
    let isActive = true;

    setAuthState('loading_profile');

    const bootstrapAuth = async () => {
      try {
        await appInstanceService.getOrCreateTmaAppInstanceId();

        if (!isActive) {
          return;
        }

        if (!tokenService.hasToken()) {
          const signInResult = await signInTma();

          if (!isActive) {
            return;
          }

          if (!signInResult.success) {
            if (signInResult.reason === AuthFailureReason.NOT_REGISTERED) {
              setAuthState('not_registered');
              navigate({ to: unregisteredRoute() });

              return;
            }

            if (
              signInResult.reason === AuthFailureReason.NETWORK_ERROR &&
              showMaintenance(signInResult.error)
            ) {
              return;
            }

            setAuthState('error');

            return;
          }
        }

        setIsAuthReady(true);
      } catch (_error) {
        if (!isActive) {
          return;
        }

        if (showMaintenance(_error)) {
          return;
        }

        setAuthState('error');
      }
    };

    void bootstrapAuth();

    return () => {
      isActive = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [navigate, showMaintenance]);

  useEffect(() => {
    if (authState === 'ready' && window.location.pathname === '/sign-up') {
      navigate({ to: nextRouteAfterAuth() });
    }

    if (
      authState === 'loading_profile' &&
      isAuthReady &&
      !dataPostAuth &&
      !errorPostAuth
    ) {
      void triggerPostAuth();
    }
  }, [
    authState,
    dataPostAuth,
    errorPostAuth,
    isAuthReady,
    navigate,
    triggerPostAuth,
  ]);

  useEffect(() => {
    // Nothing is stored on the retry screen. Returning here also keeps a
    // refused response from being reported again when the state changes.
    if (!dataPostAuth || authState === 'error') return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!dataPostAuth.registered) {
      if (authState === 'loading_profile' || authState === 'initializing') {
        setAuthState('not_registered');
        navigate({ to: unregisteredRoute() });
      }

      return;
    }

    // A response without a user is incomplete: keep everything the store has.
    // On the first load there is nothing to keep, so show the retry screen
    // instead of an app without a profile.
    if (!isStorableUser(dataPostAuth.user)) {
      reportRefusedUser('AuthProvider', dataPostAuth.user);

      if (authState === 'loading_profile' || authState === 'initializing') {
        setAuthState('error');
      }

      return;
    }

    applyAuthResponse(dataPostAuth);

    if (!dataPostAuth.user.is_premium) {
      // Read current filter state ad-hoc; subscribing to the store would
      // re-trigger this effect on every setFilters call (see comment near
      // the selector above).
      const { filters: currentFilters, getFiltersCount } =
        useFiltersStore.getState();
      if (getFiltersCount() > 0) {
        setFilters({
          ...currentFilters,
          ...defaultPremiumFilters,
        });
      }
    }

    schedulePremiumRefresh(dataPostAuth.user.premium_until);

    if (authState !== 'ready') {
      setAuthState('ready');
    }
  }, [authState, dataPostAuth, navigate, setFilters, schedulePremiumRefresh]);

  useEffect(() => {
    if (authState !== 'loading_profile' || !errorPostAuth) return;

    if (showMaintenance(errorPostAuth)) {
      return;
    }

    setAuthState('error');
  }, [authState, errorPostAuth, showMaintenance]);

  if (authState === 'initializing' || authState === 'loading_profile') {
    return <Loader />;
  }

  if (authState === 'error') {
    return (
      <Result
        src="/icons/3d-icons/sad.png"
        title="Ошибка подключения"
        description="Не удалось подключиться к серверу"
        style={{ marginTop: 0 }}
        button={
          <Button
            testId="auth-provider-retry-button"
            onClick={() => window.location.reload()}
          >
            Повторить
          </Button>
        }
      />
    );
  }

  if (authState === 'maintenance') {
    return (
      <Result
        src="/icons/3d-icons/sand-clock.png"
        title="Идут технические работы"
        description={maintenanceMessage}
        style={{ marginTop: 0 }}
        button={
          <Button
            testId="auth-provider-maintenance-retry-button"
            onClick={() => window.location.reload()}
          >
            Обновить страницу
          </Button>
        }
      />
    );
  }

  if (authState === 'not_registered') {
    return children;
  }

  return children;
};
